export type HttpClientOptions = {
  data?: unknown;
  headers?: Record<string, string>;
  query?: Record<string, string>;
  timeout?: number;
};

export type HttpRequestMethod = <T = unknown>(
  url: string,
  options?: HttpClientOptions
) => Promise<T>;

export type HttpClient = {
  (input: RequestInfo | URL, init?: RequestInit): Promise<Response>;
  get: HttpRequestMethod;
  post: HttpRequestMethod;
  put: HttpRequestMethod;
  patch: HttpRequestMethod;
  delete: HttpRequestMethod;
  options: HttpRequestMethod;
  head: HttpRequestMethod;
};

export class HttpError extends Error {
  status: number;
  statusText: string;
  responseJSON: unknown;
  responseText: string;

  constructor(
    status: number,
    statusText: string,
    responseJSON: unknown,
    responseText: string
  ) {
    super(`HTTP ${status} ${statusText}`);
    this.name = 'HttpError';
    this.status = status;
    this.statusText = statusText;
    this.responseJSON = responseJSON;
    this.responseText = responseText;
  }
}

function buildUrl(url: string, query?: Record<string, string>): string {
  if (!query || Object.keys(query).length === 0) {
    return url;
  }
  const params = new URLSearchParams(query);
  return `${url}${url.includes('?') ? '&' : '?'}${params}`;
}

type TimeoutResult = {
  signal: AbortSignal;
  clear: () => void;
};

function createTimeout(ms: number): TimeoutResult {
  if (typeof AbortSignal !== 'undefined' && 'timeout' in AbortSignal) {
    return { signal: (AbortSignal as any).timeout(ms), clear: () => {} };
  }
  const controller = new (AbortController as any)();
  const id = setTimeout(() => controller.abort(), ms);
  return { signal: controller.signal, clear: () => clearTimeout(id) };
}

const NO_BODY_STATUSES = new Set([204, 205, 304]);

function buildInit(
  method: string,
  opts?: HttpClientOptions
): { init: RequestInit; requestTimeout: (() => void) | undefined } {
  const headers: Record<string, string> = {};

  if (opts?.data !== undefined) {
    headers['Content-Type'] = 'application/json';
  }

  if (opts?.headers) {
    Object.assign(headers, opts.headers);
  }

  const init: RequestInit = { method };

  let requestTimeout: (() => void) | undefined;

  if (Object.keys(headers).length > 0) {
    init.headers = headers;
  }

  if (opts?.data !== undefined) {
    init.body = JSON.stringify(opts.data);
  }

  if (opts?.timeout !== undefined) {
    const timeout = createTimeout(opts.timeout);
    init.signal = timeout.signal;
    requestTimeout = timeout.clear;
  }

  return { init, requestTimeout };
}

export function withHttpMethods(fetchFn: typeof fetch): HttpClient {
  const makeMethod =
    (method: string): HttpRequestMethod =>
    async (url, opts) => {
      const fullUrl = buildUrl(url, opts?.query);
      const { init, requestTimeout } = buildInit(method, opts);
      try {
        const response = await fetchFn(fullUrl, init);

        if (!response.ok) {
          const text = await response.text().catch(() => '');
          let json: unknown = null;
          try {
            json = JSON.parse(text);
          } catch {
            // not JSON
          }
          throw new HttpError(response.status, response.statusText, json, text);
        }

        if (
          method === 'HEAD' ||
          method === 'OPTIONS' ||
          NO_BODY_STATUSES.has(response.status) ||
          response.headers.get('content-length') === '0'
        ) {
          return null;
        }

        return response.json();
      } finally {
        requestTimeout?.();
      }
    };

  const client = ((input: RequestInfo | URL, init?: RequestInit) =>
    fetchFn(input, init)) as HttpClient;

  client.get = makeMethod('GET');
  client.post = makeMethod('POST');
  client.put = makeMethod('PUT');
  client.patch = makeMethod('PATCH');
  client.delete = makeMethod('DELETE');
  client.options = makeMethod('OPTIONS');
  client.head = makeMethod('HEAD');

  return client;
}
